import React from 'react';

import { Flex, Grid, GridItem } from '@chakra-ui/react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Icon,
  MeshMavenGlassesOnDefault as MeshMavenGlassesOnDefaultIcon,
  Metric,
  MetricCard,
  MetricCardText,
  MetricCardTitle,
  TrendingDown,
  TrendingUp,
} from '@mesh.ai/rabbit';

import BetaTag from 'uiToolkitV1/Tag/BetaTag';

import {
  GOALS_METRIC_CARDS,
  GOALS_OVERVIEW_PAGE_METRIC_CARDS,
  MODULE_KEYS_METRIC_CARDS_DEER,
  REVIEWS_METRIC_CARDS,
  DISCIPLINARY_ACTIONS_METRIC_CARDS,
  DISCIPLINARY_ACTIONS_ADMIN_REPORTS_METRIC_CARDS,
} from './constants';
import { useIsMetricCardFilterActive } from './hooks/useIsMetricCardFilterActive';
import { useMetricCardNavigateAndFilter } from './hooks/useMetricCardsNavigateAndFilter';

export interface MetricCardsData {
  cardId:
    | (typeof GOALS_OVERVIEW_PAGE_METRIC_CARDS)['myData'][keyof (typeof GOALS_OVERVIEW_PAGE_METRIC_CARDS)['myData']]
    | (typeof GOALS_OVERVIEW_PAGE_METRIC_CARDS)['allReports'][keyof (typeof GOALS_OVERVIEW_PAGE_METRIC_CARDS)['allReports']]
    | (typeof GOALS_OVERVIEW_PAGE_METRIC_CARDS)['directReports'][keyof (typeof GOALS_OVERVIEW_PAGE_METRIC_CARDS)['directReports']]
    | (typeof GOALS_OVERVIEW_PAGE_METRIC_CARDS)['company'][keyof (typeof GOALS_OVERVIEW_PAGE_METRIC_CARDS)['company']]
    | (typeof GOALS_METRIC_CARDS)[keyof typeof GOALS_METRIC_CARDS]
    | (typeof REVIEWS_METRIC_CARDS)[keyof typeof REVIEWS_METRIC_CARDS]
    | (typeof DISCIPLINARY_ACTIONS_METRIC_CARDS)[keyof typeof DISCIPLINARY_ACTIONS_METRIC_CARDS]
    | (typeof DISCIPLINARY_ACTIONS_ADMIN_REPORTS_METRIC_CARDS)[keyof typeof DISCIPLINARY_ACTIONS_ADMIN_REPORTS_METRIC_CARDS];

  title: string;
  subTitle?: string;
  color?: 'success' | 'warning' | '';
  count?: number | string;
  totalCount?: number | string;
  value?: string | number;
  isAICard?: boolean;
}

interface MetricCardsProps {
  cards: MetricCardsData[];
  columns?: string;
  cardTitle: string;
  height?: string;
  module: (typeof MODULE_KEYS_METRIC_CARDS_DEER)[keyof typeof MODULE_KEYS_METRIC_CARDS_DEER];
  gridStatePersistenceKey?: string;
  cardStyle: 'style1' | 'style2' | 'style3';
  isOverviewPageCard?: boolean;
}

const CardWithFilter = ({ data, gridStatePersistenceKey, cardStyle = 'style1' }) => {
  const isFilterActiveInPersistedFilters = useIsMetricCardFilterActive({
    persistenceKey: gridStatePersistenceKey,
    cardId: data.cardId,
  });
  const isOnTargetPage = data?.isOnTargetPage;
  const showAsSelected = isFilterActiveInPersistedFilters && (!data?.targetPage || isOnTargetPage);

  if (cardStyle === 'style1') {
    return (
      <MetricCard
        style={{ height: '100%' }}
        onFilterClick={data?.onFilterClick}
        // eslint-disable-next-line no-nested-ternary
        filterLabel={data?.onFilterClick ? (showAsSelected ? 'Remove filter' : 'Apply filter') : ''}
        filterActive={showAsSelected}
        onFilterActiveClick={data?.onFilterActiveClick}
      >
        <Flex direction="column" justify="space-between" flex="1" w="100%" h="100%" gap={2}>
          <MetricCardText>{data?.title}</MetricCardText>
          <Metric>{data?.value}</Metric>
        </Flex>
        {data?.count && (
          <Flex>
            <Icon IconSVG={data?.color === 'success' ? TrendingUp : TrendingDown} size="small" />
            <MetricCardText type={data?.color}>{`${data?.count} ${data?.subTitle}`}</MetricCardText>
          </Flex>
        )}
      </MetricCard>
    );
  }

  if (cardStyle === 'style3') {
    const { onFilterClick, onFilterActiveClick, title, count, totalCount, subTitle, isAICard, isBeta } = data;

    return (
      <MetricCard
        style={{ height: '100%' }}
        onFilterClick={onFilterClick}
        filterLabel={(onFilterClick && (showAsSelected ? 'Remove filter' : 'Apply filter')) || ''}
        filterActive={showAsSelected}
        onFilterActiveClick={onFilterActiveClick}
        isAICard={isAICard}
      >
        {title && (
          <MetricCardTitle style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {isAICard && <Icon IconSVG={MeshMavenGlassesOnDefaultIcon} height={20} width={20} />}
            {title}
            {isBeta && <BetaTag />}
          </MetricCardTitle>
        )}
        <Metric>
          {count}
          <span style={{ color: '#999999' }}> / {totalCount}</span>
        </Metric>
        {subTitle && <MetricCardText>{subTitle}</MetricCardText>}
      </MetricCard>
    );
  }

  return (
    <MetricCard
      style={{ height: '100%' }}
      onFilterClick={data?.onFilterClick}
      // eslint-disable-next-line no-nested-ternary
      filterLabel={data?.onFilterClick ? (showAsSelected ? 'Remove filter' : 'Apply filter') : ''}
      filterActive={showAsSelected}
      onFilterActiveClick={data?.onFilterActiveClick}
    >
      <Metric>
        {data?.count}/{data.totalCount}
      </Metric>

      <MetricCardTitle>{data?.title}</MetricCardTitle>
      <MetricCardText>{data?.subTitle}</MetricCardText>
    </MetricCard>
  );
};

const MetricCards = ({
  cards,
  module,
  cardTitle,
  columns,
  height,
  gridStatePersistenceKey = '',
  cardStyle = 'style1',
  isOverviewPageCard = false,
}: MetricCardsProps) => {
  const cardsWithNavigateAndFilter: MetricCardsData[] = useMetricCardNavigateAndFilter({
    cards: cards || [],
    module,
    gridStatePersistenceKey,
    isOverviewPageCard,
  });
  if (!cards || !cards.length) return null;

  return (
    <Card
      style={{
        height,
      }}
    >
      <CardHeader>
        <CardTitle>{cardTitle}</CardTitle>
      </CardHeader>
      <CardContent>
        <Grid templateColumns={columns} gap={2}>
          {cardsWithNavigateAndFilter?.map((data, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <GridItem key={index}>
              <CardWithFilter data={data} cardStyle={cardStyle} gridStatePersistenceKey={gridStatePersistenceKey} />
            </GridItem>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
};

export { MetricCards };
